import React, { useState, useEffect } from "react";
import {
  Card,
  IconButton,
  TextField,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { Add, Edit, Delete, Close, Search, Refresh, FilterList } from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Custom components
import CustomPagination from "components/CustomPagination";
import { QuestionTypeDropdown } from "components/CustomDropdown";
import { AddQuestionDialog, EditQuestionDialog } from "./components";

// API and utilities
import api from "utils/api";
import { toast } from "react-toastify";

function QuestionManagement() {
  // ==================== STATE MANAGEMENT ====================

  // Data state
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);

  // Pagination state
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Dialog state
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // ==================== API FUNCTIONS ====================

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: itemsPerPage,
        sort: "createdAt",
        order: "desc",
      };

      // Add filters if they exist
      if (searchTerm.trim()) {
        params.subject = searchTerm.trim();
      }
      if (typeFilter) {
        params.questionType = typeFilter;
      }

      const response = await api.get("/questions", { params });

      if (response.data.success) {
        setQuestions(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalQuestions(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error(error.response?.data?.message || "Failed to fetch questions");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchQuestions();
  };

  // ==================== EFFECTS ====================

  // Initial load
  useEffect(() => {
    fetchQuestions();
  }, []);

  // Load when page or items per page changes
  useEffect(() => {
    fetchQuestions();
  }, [page, itemsPerPage]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (page !== 1) {
        setPage(1); // Reset to first page when filters change
      } else {
        fetchQuestions();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, typeFilter]);

  // ==================== EVENT HANDLERS ====================

  // Delete question
  const handleDelete = async () => {
    if (!selectedQuestion?._id) return;

    setLoading(true);
    try {
      const response = await api.delete(`/questions/${selectedQuestion._id}`);
      if (response.data.success) {
        toast.success("Question deleted successfully!");
        setDeleteDialog(false);
        setSelectedQuestion(null);
        fetchQuestions();
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error(error.response?.data?.message || "Failed to delete question");
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1); // Reset to first page when changing items per page
  };

  // Filter handlers
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setTypeFilter("");
  };

  // Dialog handlers
  const handleAddClick = () => {
    setAddDialog(true);
  };

  const handleEditClick = (question) => {
    setSelectedQuestion(question);
    setEditDialog(true);
  };

  const handleDeleteClick = (question) => {
    setSelectedQuestion(question);
    setDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setAddDialog(false);
    setEditDialog(false);
    setDeleteDialog(false);
    setSelectedQuestion(null);
  };

  const handleSuccess = () => {
    fetchQuestions();
  };

  // ==================== TABLE CONFIGURATION ====================

  // Table columns with S.No
  const columns = [
    { name: "sno", align: "center" },
    { name: "question", align: "left" },
    { name: "subject", align: "left" },
    { name: "type", align: "center" },
    { name: "created", align: "center" },
    { name: "actions", align: "center" },
  ];

  // Table rows with proper serial numbering for pagination
  const rows = questions.map((question, index) => {
    const serialNumber = (page - 1) * itemsPerPage + index + 1;

    return {
      sno: (
        <ArgonBox textAlign="center">
          <ArgonTypography variant="button" fontWeight="medium" color="secondary">
            {serialNumber}
          </ArgonTypography>
        </ArgonBox>
      ),
      question: (
        <ArgonBox maxWidth={300}>
          <Tooltip title={question.question} arrow placement="top">
            <ArgonTypography variant="button" fontWeight="medium">
              {question.question.length > 60
                ? question.question.substring(0, 60) + "..."
                : question.question}
            </ArgonTypography>
          </Tooltip>
        </ArgonBox>
      ),
      subject: (
        <ArgonBox>
          <ArgonTypography variant="caption" fontWeight="medium" color="info">
            {question.subject}
          </ArgonTypography>
        </ArgonBox>
      ),
      type: (
        <Chip
          label={question.questionType}
          color={question.questionType === "VERBAL" ? "info" : "secondary"}
          size="small"
          sx={{
            fontWeight: "medium",
            fontSize: "0.75rem",
          }}
        />
      ),
      created: (
        <ArgonTypography variant="caption" color="secondary">
          {new Date(question.createdAt).toLocaleDateString()}
        </ArgonTypography>
      ),
      actions: (
        <ArgonBox display="flex" alignItems="center" gap={0.5}>
          <Tooltip title="Edit Question" arrow>
            <IconButton
              size="small"
              onClick={() => handleEditClick(question)}
              sx={{
                color: "info.main",
                "&:hover": { backgroundColor: "info.main", color: "white" },
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Question" arrow>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(question)}
              sx={{
                color: "error.main",
                "&:hover": { backgroundColor: "error.main", color: "white" },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </ArgonBox>
      ),
    };
  });

  // ==================== RENDER ====================

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            {/* ========== HEADER SECTION ========== */}
            <ArgonBox
              p={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={2}
            >
              <ArgonBox>
                <ArgonTypography variant="h5" fontWeight="bold" color="primary">
                  📝 Question Management
                </ArgonTypography>
                <ArgonTypography variant="body2" color="secondary">
                  Total {totalQuestions} questions found
                </ArgonTypography>
              </ArgonBox>

              {/* ========== SEARCH AND FILTERS ========== */}
              <ArgonBox
                display="flex"
                alignItems="center"
                gap={2}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5}>
                  {/* Search Input */}
                  <TextField
                    size="small"
                    placeholder="Search by subject..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    InputProps={{
                      startAdornment: <Search fontSize="small" sx={{ color: "text.secondary" }} />,
                      endAdornment: searchTerm && (
                        <IconButton size="small" onClick={() => handleSearchChange("")}>
                          <Close fontSize="small" />
                        </IconButton>
                      ),
                    }}
                    sx={{
                      minWidth: 220,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                    }}
                  />

                  {/* Type Filter */}
                  <QuestionTypeDropdown
                    value={typeFilter}
                    onChange={handleTypeFilterChange}
                    width="160px"
                    size="small"
                    showIcons={true}
                  />
                </ArgonBox>

                {/* ========== ACTION BUTTONS ========== */}
                <ArgonBox display="flex" gap={1}>
                  {/* <Tooltip title="Refresh Data" arrow>
                    <IconButton
                      onClick={handleRefresh}
                      disabled={loading}
                      size="small"
                      sx={{
                        backgroundColor: "secondary.light",
                        "&:hover": { backgroundColor: "secondary.main", color: "white" },
                      }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip> */}

                  {(searchTerm || typeFilter) && (
                    <Tooltip title="Clear Filters" arrow>
                      <IconButton
                        onClick={clearFilters}
                        size="small"
                        sx={{
                          backgroundColor: "warning.light",
                          "&:hover": { backgroundColor: "warning.main", color: "white" },
                        }}
                      >
                        <FilterList fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  <ArgonButton
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={handleAddClick}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Question
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {/* ========== TABLE SECTION ========== */}
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
                "& .MuiTableRow-root:hover": {
                  backgroundColor: "rgba(0,0,0,0.02)",
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={8}>
                  <CircularProgress size={40} />
                  <ArgonTypography variant="body2" color="secondary" ml={2}>
                    Loading questions...
                  </ArgonTypography>
                </ArgonBox>
              ) : questions.length === 0 ? (
                <ArgonBox display="flex" flexDirection="column" alignItems="center" p={8}>
                  <ArgonTypography variant="h6" color="secondary" mb={1}>
                    📋 No questions found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="secondary" mb={3} textAlign="center">
                    {searchTerm || typeFilter
                      ? "Try adjusting your search filters to find what you're looking for"
                      : "Get started by creating your first question"}
                  </ArgonTypography>
                  {!searchTerm && !typeFilter ? (
                    <ArgonButton
                      variant="contained"
                      color="primary"
                      startIcon={<Add />}
                      onClick={handleAddClick}
                      sx={{ borderRadius: 2 }}
                    >
                      Create First Question
                    </ArgonButton>
                  ) : (
                    <ArgonButton
                      variant="outlined"
                      color="secondary"
                      onClick={clearFilters}
                      sx={{ borderRadius: 2 }}
                    >
                      Clear Filters
                    </ArgonButton>
                  )}
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>

            {/* ========== PAGINATION SECTION ========== */}
            {totalQuestions > 0 && (
              <CustomPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalQuestions}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                loading={loading}
              />
            )}
          </Card>
        </ArgonBox>

        {/* ========== ADD QUESTION DIALOG ========== */}
        <AddQuestionDialog
          open={addDialog}
          onClose={handleCloseDialogs}
          onSuccess={handleSuccess}
        />

        {/* ========== EDIT QUESTION DIALOG ========== */}
        <EditQuestionDialog
          open={editDialog}
          onClose={handleCloseDialogs}
          onSuccess={handleSuccess}
          question={selectedQuestion}
        />

        {/* ========== DELETE CONFIRMATION DIALOG ========== */}
        <Dialog
          open={deleteDialog}
          onClose={!loading ? handleCloseDialogs : undefined}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
              <ArgonBox>
                <ArgonTypography variant="h6" color="error">
                  🗑️ Delete Question
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary">
                  This action cannot be undone
                </ArgonTypography>
              </ArgonBox>
              {!loading && (
                <IconButton onClick={handleCloseDialogs}>
                  <Close />
                </IconButton>
              )}
            </ArgonBox>
          </DialogTitle>

          <DialogContent dividers>
            {selectedQuestion && (
              <ArgonBox>
                <ArgonTypography variant="body1" mb={2}>
                  Are you sure you want to delete this question?
                </ArgonTypography>
                <ArgonBox
                  p={2}
                  backgroundColor="rgba(255,0,0,0.05)"
                  borderRadius={2}
                  border="1px solid rgba(255,0,0,0.2)"
                >
                  <ArgonTypography variant="caption" color="secondary" display="block">
                    Question:
                  </ArgonTypography>
                  <ArgonTypography variant="body2" fontWeight="medium">
                    {selectedQuestion.question}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary" mt={1} display="block">
                    Subject: {selectedQuestion.subject} | Type: {selectedQuestion.questionType}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <ArgonButton variant="outlined" onClick={handleCloseDialogs} disabled={loading}>
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Delete />}
              sx={{ minWidth: 120 }}
            >
              {loading ? "Deleting..." : "Delete"}
            </ArgonButton>
          </DialogActions>
        </Dialog>
      </ArgonBox>

      <Footer />
    </DashboardLayout>
  );
}

export default QuestionManagement;

